import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Phone, MessageSquare, Facebook, Gift } from 'lucide-react';
import RSVPForm from '../components/RSVPForm';
import { supabase } from '../lib/supabase';
import { ThemeProvider } from '../components/ThemeProvider';
import ThemedLayout from '../components/ThemedLayout';
import type { Registry, RegistryEvent } from '../types/registry';
import type { ThemeConfig } from '../types/theme';

export default function RSVPPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [registry, setRegistry] = useState<Registry | null>(null);
  const [events, setEvents] = useState<RegistryEvent[]>([]);
  const [theme, setTheme] = useState<ThemeConfig>({
    backgroundImage: '',
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    opacity: 0.75,
    fontFamily: 'IntroRust'
  });

  useEffect(() => {
    if (id) {
      loadRegistry();
      loadEvents();
    }
  }, [id]);

  const loadRegistry = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData.session?.user?.id;

      const { data, error } = await supabase
        .from('registries')
        .select('*, theme')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (data) {
        // Owner should manage their registry directly, not go through RSVP
        if (currentUserId && currentUserId === data.user_id) {
          navigate(`/registries/${id}`, { replace: true });
          return;
        }
        setRegistry(data);
        if (data.theme) {
          const defaultTheme = {
            backgroundImage: '',
            textColor: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            opacity: 0.75,
            fontFamily: 'IntroRust'
          };

          const normalizedTheme = {
            ...defaultTheme,
            ...data.theme,
            backgroundImage: data.theme.backgroundImage || defaultTheme.backgroundImage
          };

          setTheme(normalizedTheme);
        }
      }
    } catch (error) {
      console.error('Error loading registry:', error);
      navigate('/');
    }
  };

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('registry_events')
        .select('*')
        .eq('registry_id', id)
        .order('date', { ascending: true });

      if (error) throw error;
      if (data) {
        setEvents(data);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  if (!registry) return null;

  return (
    <ThemeProvider initialTheme={theme}>
      <ThemedLayout className="min-h-screen flex flex-col">
        <div className="flex-1">
          <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="text-center mb-12">
              <h1 className="text-3xl font-bold mb-4">{registry.title}</h1>
              {registry.description && (
                <p className="text-lg opacity-90 mb-8">{registry.description}</p>
              )}
            </div>

            <RSVPForm registryId={id!} events={events} />

            <div className="mt-8 text-center">
              <p className="text-sm opacity-60 mb-3">Already RSVPed or want to browse gifts first?</p>
              <button
                onClick={() => navigate(`/registries/${id}`)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium backdrop-blur-sm"
              >
                <Gift className="w-4 h-4" />
                Skip to Gift Registry
              </button>
            </div>
          </div>
        </div>

        <footer className="relative mt-auto py-8 bg-gray-900/80 backdrop-blur-sm border-t border-gray-800 z-10">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center space-y-6">
              <img src="/white-no-logo.png" alt="Giftyfy" className="h-20" style={{ mixBlendMode: 'screen' }} />
              <p className="text-gray-300">
                Making gift-giving delightful and memorable
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <a 
                  href="tel:+94779257975" 
                  className="relative z-20 inline-flex items-center px-4 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-gray-300 hover:text-white cursor-pointer"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  <span>+94 779257975</span>
                </a>
                <a 
                  href="https://wa.me/94779257975" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="relative z-20 inline-flex items-center px-4 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-gray-300 hover:text-white cursor-pointer"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  <span>WhatsApp</span>
                </a>
                <a 
                  href="https://web.facebook.com/GiftyfyOfficial" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="relative z-20 inline-flex items-center px-4 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-gray-300 hover:text-white cursor-pointer"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  <span>Facebook</span>
                </a>
              </div>
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Giftyfy. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </ThemedLayout>
    </ThemeProvider>
  );
}